/**
 * Restore flow — "reopen a saved session's tabs" with confirm → open → Undo.
 * Design source: design-handoff/tabvault-ds/ui_kits/vault/Vault Restore Flow.dc.html
 *
 * PUBLIC API (stable — the orchestrator wires triggers against this):
 *
 *   const { start, node } = useRestoreFlow();
 *
 *   start(session: { title: string | null; items: Array<{ url: string; title: string }> }): void
 *     Opens the confirm dialog for that session. Call it from any click
 *     handler (SweepCard "Restore", PageDetail, search results…).
 *
 *   node: ReactNode
 *     The modal + toast layer. Render it ONCE near the app root (it is
 *     position:fixed, so anywhere non-transformed works).
 *
 * Usage:
 *   const restore = useRestoreFlow();
 *   <Button onClick={() => restore.start(sessionFromSweep(sweep, items))}>Restore</Button>
 *   …
 *   {restore.node}
 *
 * Behaviour notes:
 *   - Everything is client-side `window.open` — no backend involvement, and
 *     restoring never deletes anything from the vault.
 *   - ≤ BATCH_THRESHOLD tabs open synchronously inside the confirm click
 *     (single user gesture → best odds with pop-up blockers). Bigger sessions
 *     open in throttled batches with an "Opening k / N…" progress card.
 *   - Pop-up blockers may null some handles; we count those, keep going, and
 *     surface a warn toast ("Allow pop-ups to restore all tabs").
 *   - "In a new window" is best-effort: scripts can't append tabs to another
 *     window, so the first tab opens with window features (forcing a separate
 *     window) and the rest open as regular tabs.
 *   - Undo (available for UNDO_SECONDS) closes the just-opened tabs via the
 *     collected WindowProxy handles — best-effort, cross-origin-safe.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { BROWSER_COLORS, Button, FaviconStack, Icon, Modal, Toast } from '@vault/ui';
import type { BrowserName, ToastTone } from '@vault/ui';
import {
  BATCH_DELAY_MS,
  BATCH_SIZE,
  BATCH_THRESHOLD,
  NEW_WINDOW_FEATURES,
  SAFETY_CAP,
  UNDO_SECONDS,
  browserTitle,
  detectBrowser,
  stackLetters,
  tabsWord,
} from './helpers.js';
import type { RestoreSession, RestoreTarget } from './helpers.js';

export type { RestoreItem, RestoreSession, RestoreTarget } from './helpers.js';
export { sessionFromSweep } from './helpers.js';

// ── flow state ──────────────────────────────────────────────────────────────
type Phase =
  | { kind: 'idle' }
  | { kind: 'confirm'; session: RestoreSession; target: RestoreTarget }
  | { kind: 'opening'; session: RestoreSession; target: RestoreTarget; opened: number; total: number }
  | { kind: 'done'; target: RestoreTarget; total: number; openedCount: number; blockedCount: number; secondsLeft: number };

interface Notice {
  tone: ToastTone;
  title: string;
  description?: string;
}

export interface RestoreFlowApi {
  /** Open the confirm dialog for a session. Safe to call from any handler. */
  start: (session: RestoreSession) => void;
  /** Modal + toast layer — mount once near the app root. */
  node: ReactNode;
}

// ── the hook ────────────────────────────────────────────────────────────────
export function useRestoreFlow(): RestoreFlowApi {
  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [notice, setNotice] = useState<Notice | null>(null);

  /** WindowProxy handles of tabs we opened — Undo's working set. */
  const handlesRef = useRef<Window[]>([]);
  const blockedRef = useRef(0);
  const cancelRef = useRef(false);
  /** Re-entry guard: a double-click on "Restore" must not open tabs twice. */
  const confirmedRef = useRef(false);
  const batchTimerRef = useRef<number | null>(null);
  const noticeTimerRef = useRef<number | null>(null);

  const clearBatchTimer = useCallback(() => {
    if (batchTimerRef.current !== null) {
      window.clearTimeout(batchTimerRef.current);
      batchTimerRef.current = null;
    }
  }, []);

  // clean up pending timers on unmount
  useEffect(
    () => () => {
      if (batchTimerRef.current !== null) window.clearTimeout(batchTimerRef.current);
      if (noticeTimerRef.current !== null) window.clearTimeout(noticeTimerRef.current);
    },
    [],
  );

  const showNotice = useCallback((n: Notice) => {
    setNotice(n);
    if (noticeTimerRef.current !== null) window.clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = window.setTimeout(() => setNotice(null), 3200);
  }, []);

  // ── entry point ───────────────────────────────────────────────────────────
  const start = useCallback(
    (session: RestoreSession) => {
      clearBatchTimer();
      cancelRef.current = true; // abandon any in-flight batch loop
      confirmedRef.current = false;
      if (session.items.length === 0) {
        showNotice({ tone: 'info', title: 'Nothing to restore', description: 'This session has no saved tabs.' });
        return;
      }
      setPhase({ kind: 'confirm', session, target: 'new' });
    },
    [clearBatchTimer, showNotice],
  );

  const dismiss = useCallback(() => setPhase({ kind: 'idle' }), []);

  // ── opening machinery ─────────────────────────────────────────────────────
  /** Opens items[from..to). Nulls (pop-up blocked) are counted, not fatal. */
  const openRange = useCallback((session: RestoreSession, target: RestoreTarget, from: number, to: number) => {
    for (let i = from; i < to; i += 1) {
      const item = session.items[i];
      if (!item) continue;
      // "New window" is best-effort: only a features-bearing open creates a
      // separate window; scripts can't push later tabs into it.
      const features = target === 'new' && i === 0 ? NEW_WINDOW_FEATURES : undefined;
      let w: Window | null = null;
      try {
        w = window.open(item.url, '_blank', features);
      } catch {
        w = null;
      }
      if (w) handlesRef.current.push(w);
      else blockedRef.current += 1;
    }
  }, []);

  const finish = useCallback((target: RestoreTarget, total: number) => {
    setPhase({
      kind: 'done',
      target,
      total,
      openedCount: handlesRef.current.length,
      blockedCount: blockedRef.current,
      secondsLeft: UNDO_SECONDS,
    });
  }, []);

  const runBatch = useCallback(
    (session: RestoreSession, target: RestoreTarget, from: number) => {
      if (cancelRef.current) return;
      const total = session.items.length;
      const to = Math.min(from + BATCH_SIZE, total);
      openRange(session, target, from, to);
      if (to >= total) {
        finish(target, total);
        return;
      }
      setPhase({ kind: 'opening', session, target, opened: to, total });
      batchTimerRef.current = window.setTimeout(() => runBatch(session, target, to), BATCH_DELAY_MS);
    },
    [finish, openRange],
  );

  /** Confirm click — runs inside the user gesture, which pop-up blockers
   *  honor. Side effects live HERE, never inside a setState updater (React 18
   *  StrictMode invokes updaters twice — tabs would open twice). */
  const confirmRestore = useCallback(
    (session: RestoreSession, target: RestoreTarget) => {
      if (confirmedRef.current) return; // double-click guard
      confirmedRef.current = true;

      const total = session.items.length;
      handlesRef.current = [];
      blockedRef.current = 0;
      cancelRef.current = false;

      if (total <= BATCH_THRESHOLD) {
        // small session: everything within this click's gesture
        openRange(session, target, 0, total);
        finish(target, total);
        return;
      }

      // big session: first batch in-gesture, the rest throttled
      openRange(session, target, 0, BATCH_SIZE);
      setPhase({ kind: 'opening', session, target, opened: BATCH_SIZE, total });
      batchTimerRef.current = window.setTimeout(() => runBatch(session, target, BATCH_SIZE), BATCH_DELAY_MS);
    },
    [finish, openRange, runBatch],
  );

  const cancelOpening = useCallback(() => {
    cancelRef.current = true;
    clearBatchTimer();
    setPhase((p) =>
      p.kind === 'opening'
        ? {
            kind: 'done',
            target: p.target,
            total: p.total,
            openedCount: handlesRef.current.length,
            blockedCount: blockedRef.current,
            secondsLeft: UNDO_SECONDS,
          }
        : p,
    );
  }, [clearBatchTimer]);

  // ── undo ──────────────────────────────────────────────────────────────────
  const undo = useCallback(() => {
    let closed = 0;
    for (const w of handlesRef.current) {
      try {
        if (!w.closed) {
          w.close();
          closed += 1;
        }
      } catch {
        // handle gone or navigated somewhere we can't touch — skip
      }
    }
    handlesRef.current = [];
    setPhase({ kind: 'idle' });
    showNotice({
      tone: 'info',
      title: 'Restore undone',
      description: `Closed ${closed} ${tabsWord(closed)} — everything stays saved in your vault.`,
    });
  }, [showNotice]);

  // ── undo countdown ("9S" in the design) ───────────────────────────────────
  useEffect(() => {
    if (phase.kind !== 'done') return;
    const t = window.setInterval(() => {
      setPhase((p) => {
        if (p.kind !== 'done') return p;
        if (p.secondsLeft <= 1) return { kind: 'idle' };
        return { ...p, secondsLeft: p.secondsLeft - 1 };
      });
    }, 1000);
    return () => window.clearInterval(t);
  }, [phase.kind]);

  // once idle, the opened tabs are here to stay — drop the handles
  useEffect(() => {
    if (phase.kind === 'idle') handlesRef.current = [];
  }, [phase.kind]);

  // ── render layer ──────────────────────────────────────────────────────────
  const browser = detectBrowser();

  const node: ReactNode = (
    <>
      {phase.kind === 'confirm' && (
        <ConfirmModal
          session={phase.session}
          target={phase.target}
          browser={browser}
          onTarget={(target) => setPhase((p) => (p.kind === 'confirm' ? { ...p, target } : p))}
          onCancel={dismiss}
          onConfirm={() => confirmRestore(phase.session, phase.target)}
        />
      )}

      <div
        style={{
          position: 'fixed',
          right: 20,
          bottom: 20,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          zIndex: 60,
        }}
      >
        {phase.kind === 'opening' && (
          <ProgressCard opened={phase.opened} total={phase.total} browser={browser} onCancel={cancelOpening} />
        )}

        {phase.kind === 'done' && phase.openedCount > 0 && (
          <ResultToast
            openedCount={phase.openedCount}
            total={phase.total}
            target={phase.target}
            browser={browser}
            secondsLeft={phase.secondsLeft}
            onUndo={undo}
          />
        )}

        {phase.kind === 'done' && phase.blockedCount > 0 && (
          <Toast
            inline
            tone="warn"
            title="Allow pop-ups to restore all tabs"
            description={`${phase.blockedCount} of ${phase.total} ${tabsWord(phase.total)} ${
              phase.blockedCount === 1 ? 'was' : 'were'
            } blocked — enable pop-ups for this site and restore again.`}
          />
        )}

        {notice && <Toast inline tone={notice.tone} title={notice.title} description={notice.description} />}
      </div>
    </>
  );

  return { start, node };
}

/**
 * Render-prop convenience over the hook, for callers that prefer JSX wiring:
 *
 *   <RestoreFlow>{(start) => <Button onClick={() => start(session)}>Restore</Button>}</RestoreFlow>
 */
export function RestoreFlow({ children }: { children: (start: (session: RestoreSession) => void) => ReactNode }) {
  const { start, node } = useRestoreFlow();
  return (
    <>
      {children(start)}
      {node}
    </>
  );
}

// ── a11y helper for span-based actions (codebase idiom uses spans) ──────────
function keyActivate(fn: () => void) {
  return (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      fn();
    }
  };
}

// ── confirm modal (design frame 1) ──────────────────────────────────────────
function ConfirmModal({
  session,
  target,
  browser,
  onTarget,
  onCancel,
  onConfirm,
}: {
  session: RestoreSession;
  target: RestoreTarget;
  browser: BrowserName;
  onTarget: (t: RestoreTarget) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const n = session.items.length;
  const { letters, overflow } = stackLetters(session.items);
  const name = session.title?.trim() || 'this session';

  return (
    <Modal
      width={460}
      onClose={onCancel}
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <FaviconStack letters={letters} overflow={overflow} size={22} />
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, letterSpacing: '-.01em' }}>
              Restore “{name}”
            </span>
            <span
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 9.5,
                fontWeight: 400,
                letterSpacing: '.06em',
                color: 'var(--text-3)',
              }}
            >
              {n} {tabsWord(n).toUpperCase()} · OPENS IN {browserTitle(browser).toUpperCase()}
            </span>
          </span>
        </span>
      }
      subtitle={`Reopen ${n} ${tabsWord(n)}? This opens ${n} browser ${tabsWord(n)}.`}
      footer={
        <>
          <span
            style={{
              marginRight: 'auto',
              alignSelf: 'center',
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              letterSpacing: '.02em',
              color: 'var(--text-3)',
            }}
          >
            TABS STAY SAVED — RESTORING NEVER DELETES
          </span>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon={<Icon name="restore" size={13} color="currentColor" />}
            onClick={onConfirm}
          >
            Restore {n} {tabsWord(n)}
          </Button>
        </>
      }
    >
      {/* target picker */}
      <div
        role="radiogroup"
        aria-label="Where to open the tabs"
        style={{
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--bg)',
          border: '1px solid var(--line)',
          borderRadius: 12,
          overflow: 'hidden',
        }}
      >
        <TargetRow
          label="In a new window"
          selected={target === 'new'}
          onSelect={() => onTarget('new')}
          right={
            <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)' }}>
              RECOMMENDED
            </span>
          }
        />
        <TargetRow label="Add to this window" selected={target === 'current'} onSelect={() => onTarget('current')} />
        <TargetRow
          label="As a pinned tab group"
          selected={false}
          disabled
          last
          right={
            <span
              style={{
                marginLeft: 'auto',
                display: 'inline-flex',
                background: 'var(--amber)',
                color: 'var(--on-amber)',
                borderRadius: 5,
                padding: '1px 6px',
                fontFamily: 'var(--font-mono)',
                fontSize: 8,
                fontWeight: 700,
              }}
            >
              PRO
            </span>
          }
        />
      </div>

      {/* target browser — the one this web app runs in */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.1em', color: 'var(--text-3)' }}>
          TO
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid var(--line-accent)',
            background: 'var(--accent-tint)',
            borderRadius: 'var(--r-pill)',
            padding: '6px 12px',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <i style={{ width: 6, height: 6, borderRadius: '50%', background: BROWSER_COLORS[browser] }} />
          {browserTitle(browser)}
        </span>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, color: 'var(--text-3)' }}>THIS BROWSER</span>
      </div>

      {/* big-session safety warning */}
      {n > SAFETY_CAP && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            background: 'var(--amber-tint)',
            border: '1px solid var(--amber-line)',
            borderRadius: 10,
            padding: '9px 12px',
          }}
        >
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--amber)', flexShrink: 0 }} />
          <span style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
            Big session — this opens{' '}
            <b style={{ color: 'var(--amber-ink)', fontWeight: 600 }}>
              {n} {tabsWord(n)}
            </b>{' '}
            at once. Your browser may stutter for a few seconds.
          </span>
        </div>
      )}
    </Modal>
  );
}

function TargetRow({
  label,
  selected,
  onSelect,
  right,
  disabled = false,
  last = false,
}: {
  label: string;
  selected: boolean;
  onSelect?: () => void;
  right?: ReactNode;
  disabled?: boolean;
  last?: boolean;
}) {
  return (
    <span
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : onSelect}
      onKeyDown={disabled || !onSelect ? undefined : keyActivate(onSelect)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '11px 14px',
        borderBottom: last ? 'none' : '1px solid var(--line)',
        background: selected ? 'var(--accent-tint)' : 'transparent',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <span
        style={{
          width: 15,
          height: 15,
          borderRadius: '50%',
          boxSizing: 'border-box',
          flexShrink: 0,
          border: selected ? '5px solid var(--primary)' : '1.5px solid var(--line-strong)',
          background: selected ? 'var(--on-primary)' : 'transparent',
        }}
      />
      <span style={{ fontSize: 12.5, fontWeight: selected ? 600 : 400, color: selected ? 'var(--text-1)' : 'var(--text-2)' }}>
        {label}
      </span>
      {right}
    </span>
  );
}

// ── progress card (design frame 2, top) ─────────────────────────────────────
function ProgressCard({
  opened,
  total,
  browser,
  onCancel,
}: {
  opened: number;
  total: number;
  browser: BrowserName;
  onCancel: () => void;
}) {
  const pct = Math.round((opened / Math.max(1, total)) * 100);
  return (
    <div
      style={{
        width: 340,
        background: 'var(--surface-2)',
        border: '1px solid var(--line-strong)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-pop)',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        fontFamily: 'var(--font-body)',
        color: 'var(--text-1)',
        animation: 'tv-rise .25s cubic-bezier(.22,.61,.36,1)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            width: 14,
            height: 14,
            border: '2px solid var(--accent)',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'tv-spin .7s linear infinite',
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>Opening tabs in {browserTitle(browser)}…</span>
        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-3)' }}>
          {opened} / {total}
        </span>
      </div>
      <span style={{ display: 'block', height: 5, borderRadius: 3, background: 'var(--surface-3)', overflow: 'hidden' }}>
        <i
          style={{
            display: 'block',
            width: `${pct}%`,
            height: 5,
            borderRadius: 3,
            background: 'var(--accent)',
            transition: 'width .2s',
          }}
        />
      </span>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '.02em', color: 'var(--text-3)' }}>
          {'THROTTLED SO '}
          {browserTitle(browser).toUpperCase()}
          {' DOESN’T CHOKE'}
        </span>
        <span
          role="button"
          tabIndex={0}
          onClick={onCancel}
          onKeyDown={keyActivate(onCancel)}
          style={{
            marginLeft: 'auto',
            fontFamily: 'var(--font-mono)',
            fontSize: 9,
            letterSpacing: '.06em',
            color: 'var(--accent-ink)',
            cursor: 'pointer',
          }}
        >
          CANCEL
        </span>
      </span>
    </div>
  );
}

// ── result toast with Undo + countdown (design frame 2, middle) ─────────────
function ResultToast({
  openedCount,
  total,
  target,
  browser,
  secondsLeft,
  onUndo,
}: {
  openedCount: number;
  total: number;
  target: RestoreTarget;
  browser: BrowserName;
  secondsLeft: number;
  onUndo: () => void;
}) {
  const title =
    openedCount === total
      ? `${total} ${tabsWord(total)} restored to ${browserTitle(browser)}`
      : `${openedCount} of ${total} tabs restored`;
  return (
    <div
      style={{
        width: 340,
        background: 'var(--surface-2)',
        border: '1px solid var(--line-strong)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-pop)',
        padding: '14px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        fontFamily: 'var(--font-body)',
        color: 'var(--text-1)',
        animation: 'tv-rise .25s cubic-bezier(.22,.61,.36,1)',
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--success)', flexShrink: 0 }} />
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{title}</span>
        <span style={{ fontSize: 11.5, color: 'var(--text-2)' }}>
          {target === 'new' ? 'New window' : 'This window'} · session kept in vault
        </span>
      </span>
      <span
        role="button"
        tabIndex={0}
        onClick={onUndo}
        onKeyDown={keyActivate(onUndo)}
        style={{ fontSize: 12, fontWeight: 600, color: 'var(--primary)', cursor: 'pointer', whiteSpace: 'nowrap' }}
      >
        Undo
      </span>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-3)' }}>{secondsLeft}S</span>
    </div>
  );
}
